import equal from 'fast-deep-equal';
import { createDraftSafeSelector } from '@reduxjs/toolkit';

import { getLoginData } from 'modules/auth/selectors';
import { getConnectionTypes, getContext, getCustomerType } from 'modules/dealers/selectors';
import {
  formAdapterSelectors, getNewAcceptedExtra,
  getNewAcceptedProactiveWorks,
  getVehicleCheckConfig,
  isMobilityAccepted,
} from 'modules/form/selectors';

import formsAdapter from 'modules/form/adapter';
import FormKeys, { SIGNATURE_IGNORED_FORM_KEYS } from 'modules/form/types/FormKeys';
import { filterObjectProperties } from 'modules/selfServices/utils';
import { reconciliateForms } from 'modules/form/conciliateUtils';
import { getWorkflows } from 'modules/refData/selectors';
import { SelfServiceStepConfig } from 'modules/selfServices/types/SelfServiceStepConfig';

import { getFlowType, getIsAdditionalCustomerDriving, getSelectedSelfService, getSummarySignatureEnabled, isAdditionalCustomerEnabled } from './selectors';

export const hasSelfServiceBeenEdited = createDraftSafeSelector(
  formAdapterSelectors.selectEntities,
  getSelectedSelfService,
  getLoginData,
  (formData, selfService, loginData) => {
    if (selfService) {
      /** @description Represents the form as it was at the beginning of the flow */
      const originalForm = reconciliateForms(selfService, loginData, formsAdapter.getInitialState().entities);

      return Object.values(formData as { [key in FormKeys]?: { key: FormKeys, values: unknown } }).filter(Boolean).reduce(
        (acc, { key, values }) => {
          if (!acc && !SIGNATURE_IGNORED_FORM_KEYS.includes(key)) {
            const newValues = filterObjectProperties(values);
            const oldValues = filterObjectProperties(originalForm[key]?.values);

            return !equal(newValues, oldValues);
          }

          return acc;
        },
        false,
      );
    }

    return false;
  },
);

export const shouldDisplayBasketStep = createDraftSafeSelector(
  getNewAcceptedExtra,
  getSelectedSelfService,
  getNewAcceptedProactiveWorks,
  (newAcceptedExtra, selfService, newAcceptedProactiveWorks) => (
    newAcceptedExtra.length > 0 || selfService?.repairOrder?.length > 0 || newAcceptedProactiveWorks.length > 0
  ),
);

export const getStepsConfig = createDraftSafeSelector(
  getVehicleCheckConfig,
  isMobilityAccepted,
  getFlowType,
  shouldDisplayBasketStep,
  getContext,
  getSummarySignatureEnabled,
  hasSelfServiceBeenEdited,
  getWorkflows,
  getIsAdditionalCustomerDriving,
  isAdditionalCustomerEnabled,
  getConnectionTypes,
  getCustomerType,
  (
    vehicleCheckConfig,
    hasAcceptedMobility,
    flowType,
    displayBasket,
    context,
    isSummarySignatureEnabled,
    pristine,
    workflows,
    isAdditionalCustomerDriving,
    additionalCustomerEnabled,
    connectionTypes,
    customerType,
  ) => ({
    ...vehicleCheckConfig,
    flowType,
    workflows,
    displayBasket,
    hasAcceptedMobility,
    isSummarySignatureEnabled,
    pristine: !pristine,
    kioskType: context?.kioskType,
    kioskModel: context?.kioskModel,
    connectionTypes: connectionTypes,
    selfServiceAvailableTypes: context?.selfServiceAvailableTypes,
    isAdditionalCustomerDriving,
    additionalCustomerEnabled,
    customerType,
  } as SelfServiceStepConfig),
);
